import { SaveOutlined } from "@ant-design/icons";
import { Alert, Button, Form, Input, Space } from "antd";
import { AxiosInstance } from "axios";
import { FunctionComponent, useCallback, useEffect, useMemo } from "react";
import { useStateIfMounted } from "use-state-if-mounted";
import ActionBtn from "../../shared/action-btn";
import DeleteBtn from "../../shared/delete-btn";
import getErrorMessage from "../../shared/get-error-msg";
import RejectBtn from "../../shared/reject-btn";
import UserModel from "../../shared/user-model";
import UserStatus from "../../shared/user-status";
import RoleSelector from "./role-selector";
import UserStatusSelector from "./user-status-selector";

interface UserFormProps {
  initialValue?: Partial<UserModel>;
  onUpdate: (value: UserModel) => void;
  onDelete?: (value: UserModel) => void;
  httpClient: AxiosInstance | any;
  disableDelete?: boolean;
}
enum Mode {
  create,
  edit,
}
const UserForm: FunctionComponent<UserFormProps> = ({
  initialValue,
  onUpdate,
  onDelete,
  httpClient,
  disableDelete,
}) => {
  const [busy, setBusy] = useStateIfMounted(false);
  const [inviting, setInviting] = useStateIfMounted(false);
  const [serverMsg, setServerMsg] = useStateIfMounted<string | undefined>(
    undefined
  );
  const [serverMsgType, setServerMsgType] = useStateIfMounted<
    "error" | "success" | undefined
  >(undefined);
  const [form] = Form.useForm();
  const mode: Mode = useMemo(() => {
    return initialValue?.id ? Mode.edit : Mode.create;
  }, [initialValue]);
  const save = useCallback(
    async (values: Partial<UserModel>) => {
      const { id, role, ...sanitizedValues } = values;
      sanitizedValues.role_id = role?.id ?? null;
      setBusy(true);
      setServerMsg(undefined);
      try {
        const { data } =
          mode === Mode.create
            ? await httpClient.post("user/invite", sanitizedValues)
            : await httpClient.patch(
              `user/${initialValue?.id}`,
              sanitizedValues
            );

        if (data?.data) {
          setServerMsgType("success");
          setServerMsg(
            mode === Mode.create ? "Invitation sent!" : "User saved!"
          );
          onUpdate(data.data);
        }
      } catch (error: any) {
        if (error?.response?.data?.error) {
          setServerMsgType("error");
          setServerMsg(getErrorMessage(error?.response?.data));
        }
      }
      setBusy(false);
    },
    [mode, onUpdate, initialValue]
  );
  useEffect(() => {
    form.setFieldsValue({
      ...initialValue,
    });
  }, [initialValue?.updated_at]);
  const inviteAgain = useCallback(async () => {
    setInviting(true);
    try {
      await httpClient.post(`user/invite/${initialValue?.id}`);
      setServerMsgType("success");
      setServerMsg("Invitation sent!");
    } catch (error: any) {
      if (error?.response?.data?.error) {
        setServerMsgType("error");
        setServerMsg(getErrorMessage(error?.response?.data));
      }
    }
    setInviting(false);
  }, [initialValue?.id]);
  return (
    <Form
      name="basic"
      labelCol={{ span: 4 }}
      wrapperCol={{ span: 20 }}
      onFinish={save}
      autoComplete="off"
      form={form}
      initialValues={{
        ...initialValue,
      }}
    >
      {serverMsg && (
        <Alert
          type={serverMsgType}
          message={serverMsg}
          style={{ marginBottom: 10 }}
        />
      )}
      <Form.Item
        label="Name"
        name="name"
        rules={[{ required: true, message: "User name required" }]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        label="Email"
        name="email"
        rules={[{ required: true, message: "User email required" }]}
      >
        <Input />
      </Form.Item>
      <Form.Item label="Role" name="role">
        <RoleSelector />
      </Form.Item>
      {mode === Mode.edit && (
        <Form.Item label="Status" name="user_status">
          <UserStatusSelector />
        </Form.Item>
      )}

      <Form.Item
        wrapperCol={{ sm: { offset: 4, span: 16 }, xs: { offset: 0 } }}
      >
        <Space>
          <ActionBtn
            type="primary"
            size="small"
            htmlType="submit"
            busy={busy}
            icon={<SaveOutlined />}
          >
            {mode === Mode.create ? "Invite" : "Save"}
          </ActionBtn>
          {mode === Mode.edit && !disableDelete && (
            <DeleteBtn
              path={`user/${initialValue?.id}`}
              onDone={() => {
                onDelete && onDelete(initialValue as any);
              }}
              httpClient={httpClient}
            />
          )}
          {mode === Mode.edit && initialValue?.user_status === UserStatus.Pending && !disableDelete && (
            <RejectBtn
              path={`user/${initialValue?.id}/reject`}
              onDone={() => {
                onDelete && onDelete(initialValue as any);
              }}
              httpClient={httpClient}
            />
          )}
          {mode === Mode.edit &&
            initialValue?.user_status === UserStatus.Invited && (
              <Button
                type="dashed"
                htmlType="button"
                disabled={busy || inviting}
                onClick={inviteAgain}
                loading={inviting}
              >
                Invite Again
              </Button>
            )}
        </Space>
      </Form.Item>
    </Form>
  );
};

export default UserForm;
